// Draws the ambient effects described in tokenRecipes.ts.
// One TokenFx belongs to one token on the table and is updated every frame.
import * as THREE from 'three';
import { cardFlat } from './cardShape.ts';
import { CONFIG } from '../../config.ts';
import { fxTextures } from './effects.ts';
import type { FxPart, MoteShape, TokenRecipe } from './tokenRecipes.ts';

const CW = CONFIG.card.width;
const CH = CONFIG.card.height;

export interface FxFrame {
  time: number; // seconds
  value: number; // the counter's number
  k: number; // 0..1 strength from the counter (1 for tokens that don't scale)
  card: THREE.Object3D | null; // the card the token is sitting on
}

// ---------------- small drawn textures ----------------

function canvasTex(w: number, h: number, draw: (ctx: CanvasRenderingContext2D) => void) {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  draw(c.getContext('2d')!);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/** A tiny repeatable random-number maker, so drawn textures look the same every time. */
function seeded(seed: number) {
  let s = seed;
  return () => ((s = (s * 16807) % 2147483647) / 2147483647);
}

const shapes = new Map<string, THREE.Texture>();
function shapeTex(shape: MoteShape | 'frost' | 'cracks' | 'line'): THREE.Texture {
  let t = shapes.get(shape);
  if (t) return t;
  if (shape === 'dot') t = fxTextures().dot;
  else if (shape === 'heart') {
    t = canvasTex(64, 64, (ctx) => {
      ctx.fillStyle = '#fff';
      ctx.beginPath();
      ctx.moveTo(32, 56);
      ctx.bezierCurveTo(4, 36, 6, 10, 22, 10);
      ctx.bezierCurveTo(28, 10, 32, 15, 32, 20);
      ctx.bezierCurveTo(32, 15, 36, 10, 42, 10);
      ctx.bezierCurveTo(58, 10, 60, 36, 32, 56);
      ctx.fill();
    });
  } else if (shape === 'z') {
    t = canvasTex(64, 64, (ctx) => {
      ctx.fillStyle = '#fff';
      ctx.font = '800 56px "Alegreya Sans", "Trebuchet MS", sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('Z', 32, 36);
    });
  } else if (shape === 'drop') {
    t = canvasTex(64, 64, (ctx) => {
      ctx.fillStyle = '#fff';
      ctx.beginPath();
      ctx.moveTo(6, 32);
      ctx.quadraticCurveTo(30, 14, 46, 18);
      ctx.arc(44, 32, 14, -Math.PI / 2, Math.PI / 2);
      ctx.quadraticCurveTo(30, 50, 6, 32);
      ctx.fill();
    });
  } else if (shape === 'bolt') {
    t = canvasTex(64, 64, (ctx) => {
      ctx.fillStyle = '#fff';
      ctx.shadowColor = '#fff';
      ctx.shadowBlur = 6;
      ctx.beginPath();
      for (const [x, y] of [[36, 4], [14, 36], [28, 36], [22, 60], [50, 26], [34, 26]] as const) ctx.lineTo(x, y);
      ctx.closePath();
      ctx.fill();
    });
  } else if (shape === 'flake') {
    t = canvasTex(64, 64, (ctx) => {
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 4;
      ctx.lineCap = 'round';
      ctx.translate(32, 32);
      for (let i = 0; i < 3; i++) {
        ctx.rotate(Math.PI / 3);
        ctx.beginPath();
        ctx.moveTo(-24, 0);
        ctx.lineTo(24, 0);
        for (const s of [-1, 1]) {
          ctx.moveTo(s * 14, 0);
          ctx.lineTo(s * 21, -7);
          ctx.moveTo(s * 14, 0);
          ctx.lineTo(s * 21, 7);
        }
        ctx.stroke();
      }
    });
  } else if (shape === 'spike') {
    t = canvasTex(64, 64, (ctx) => {
      ctx.fillStyle = '#fff';
      ctx.beginPath();
      ctx.moveTo(4, 22);
      ctx.lineTo(60, 32);
      ctx.lineTo(4, 42);
      ctx.closePath();
      ctx.fill();
    });
  } else if (shape === 'arrow') {
    // a down arrow with a dark edge, so it reads on light and dark cards alike
    t = canvasTex(64, 64, (ctx) => {
      ctx.beginPath();
      for (const [x, y] of [[24, 6], [40, 6], [40, 32], [54, 32], [32, 58], [10, 32], [24, 32]] as const) ctx.lineTo(x, y);
      ctx.closePath();
      ctx.fillStyle = '#fff';
      ctx.fill();
      ctx.lineJoin = 'round';
      ctx.lineWidth = 4;
      ctx.strokeStyle = '#3a3050';
      ctx.stroke();
    });
  } else if (shape === 'line') {
    t = canvasTex(8, 32, (ctx) => {
      const g = ctx.createLinearGradient(0, 0, 0, 32);
      g.addColorStop(0, 'rgba(255,255,255,0)');
      g.addColorStop(0.5, 'rgba(255,255,255,1)');
      g.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, 8, 32);
    });
  } else if (shape === 'frost') {
    t = canvasTex(256, 358, (ctx) => {
      const rnd = seeded(7);
      // icy rim, clear in the middle
      const g = ctx.createRadialGradient(128, 179, 60, 128, 179, 215);
      g.addColorStop(0, 'rgba(255,255,255,0)');
      g.addColorStop(0.7, 'rgba(255,255,255,0.25)');
      g.addColorStop(1, 'rgba(255,255,255,0.95)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, 256, 358);
      // feathery crystals growing in from the edges
      ctx.strokeStyle = 'rgba(255,255,255,0.85)';
      ctx.lineCap = 'round';
      for (let i = 0; i < 46; i++) {
        const side = Math.floor(rnd() * 4);
        let x = side === 0 ? 0 : side === 1 ? 256 : rnd() * 256;
        let y = side === 2 ? 0 : side === 3 ? 358 : rnd() * 358;
        let a = Math.atan2(179 - y, 128 - x) + (rnd() - 0.5) * 1.2;
        ctx.lineWidth = 1 + rnd() * 1.6;
        ctx.beginPath();
        ctx.moveTo(x, y);
        const steps = 3 + Math.floor(rnd() * 4);
        for (let j = 0; j < steps; j++) {
          a += (rnd() - 0.5) * 0.9;
          const len = 8 + rnd() * 16;
          x += Math.cos(a) * len;
          y += Math.sin(a) * len;
          ctx.lineTo(x, y);
          // side branch
          const b = a + (rnd() < 0.5 ? 1 : -1) * 0.9;
          ctx.moveTo(x, y);
          ctx.lineTo(x + Math.cos(b) * len * 0.6, y + Math.sin(b) * len * 0.6);
          ctx.moveTo(x, y);
        }
        ctx.stroke();
      }
    });
  } else {
    // cracks
    t = canvasTex(256, 358, (ctx) => {
      const rnd = seeded(23);
      ctx.strokeStyle = '#fff';
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      for (let i = 0; i < 7; i++) {
        let x = 30 + rnd() * 196;
        let y = 30 + rnd() * 298;
        let a = rnd() * Math.PI * 2;
        const walk = (width: number, steps: number) => {
          ctx.lineWidth = width;
          ctx.beginPath();
          ctx.moveTo(x, y);
          for (let j = 0; j < steps; j++) {
            a += (rnd() - 0.5) * 1.3;
            x += Math.cos(a) * (10 + rnd() * 22);
            y += Math.sin(a) * (10 + rnd() * 22);
            ctx.lineTo(x, y);
          }
          ctx.stroke();
        };
        const [sx, sy, sa] = [x, y, a];
        walk(2.6, 6);
        [x, y, a] = [sx, sy, sa + 2 + rnd()];
        walk(1.6, 4);
      }
    });
  }
  shapes.set(shape, t);
  return t;
}

function flat(w: number, h: number) {
  const g = new THREE.PlaneGeometry(w, h);
  g.rotateX(-Math.PI / 2);
  return g;
}

const ease = (t: number) => 1 - Math.pow(1 - t, 3);
/** How much of a layer is left at strength k, given how strongly it scales. */
const scaled = (grow: number | undefined, k: number) => 1 - (grow ?? 0) + (grow ?? 0) * k;

// =====================================================================

export class TokenFx {
  /** follows the token */
  readonly group = new THREE.Group();
  /** sits on the card under the token (tints, domes, arcs) */
  private cardGroup = new THREE.Group();
  private card: THREE.Object3D | null = null;
  private updaters: ((f: FxFrame) => void)[] = [];
  private owned: (THREE.Mesh | THREE.Sprite)[] = [];
  private seed = Math.random() * 100;
  private size: number;
  private face: THREE.Object3D;
  private top: number;

  /**
   * @param size the token's width in cm
   * @param face the token's own picture (so "jitter" can shake it)
   * @param top how high the token's top surface is above its base
   */
  constructor(recipe: TokenRecipe, size: number, face: THREE.Object3D, top: number) {
    this.size = size;
    this.face = face;
    this.top = top;
    this.cardGroup.visible = false;
    for (const part of recipe.parts) this.build(part);
  }

  private mesh(geo: THREE.BufferGeometry, map: THREE.Texture | null, color: string, solid = false, parent: THREE.Object3D = this.group) {
    const m = new THREE.Mesh(
      geo,
      new THREE.MeshBasicMaterial({
        map,
        color,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        blending: solid ? THREE.NormalBlending : THREE.AdditiveBlending,
      }),
    );
    parent.add(m);
    this.owned.push(m);
    return m;
  }

  private sprite(map: THREE.Texture, color: string, solid = false) {
    const sp = new THREE.Sprite(
      new THREE.SpriteMaterial({ map, color, transparent: true, opacity: 0, depthWrite: false, blending: solid ? THREE.NormalBlending : THREE.AdditiveBlending }),
    );
    this.group.add(sp);
    this.owned.push(sp);
    return sp;
  }

  private build(part: FxPart) {
    const s = this.size;
    const seed = this.seed;
    const tex = fxTextures();

    switch (part.t) {
      case 'aura': {
        const m = this.mesh(flat(2, 2), tex.dot, part.color, part.solid);
        m.position.y = 0.02;
        const a = new THREE.Color(part.color);
        const b = new THREE.Color(part.color2 ?? part.color);
        const speed = part.speed ?? 0.4;
        this.updaters.push((f) => {
          const v = scaled(part.grow, f.k);
          const wave = Math.sin((f.time + seed) * speed * Math.PI * 2);
          const size = part.r * s * (0.55 + 0.45 * v) * (1 + 0.06 * wave);
          m.scale.set(size, 1, size);
          const mat = m.material as THREE.MeshBasicMaterial;
          mat.color.copy(a).lerp(b, 0.5 + 0.5 * Math.sin((f.time + seed) * speed * Math.PI));
          mat.opacity = part.opacity * (0.75 + 0.25 * wave) * (0.35 + 0.65 * v);
        });
        break;
      }
      case 'ripple': {
        const m = this.mesh(flat(2, 2), tex.circle, part.color);
        m.position.y = 0.03;
        this.updaters.push((f) => {
          const v = scaled(part.grow, f.k);
          const u = ((f.time + seed) % part.every) / 1.7;
          m.visible = u < 1;
          if (!m.visible) return;
          const size = s * (0.6 + (part.r * (0.45 + 0.55 * v) - 0.6) * ease(u));
          m.scale.set(size, 1, size);
          (m.material as THREE.MeshBasicMaterial).opacity = part.opacity * (1 - u) * (0.4 + 0.6 * v);
        });
        break;
      }
      case 'motes':
        this.buildMotes(part);
        break;
      case 'halo': {
        const ring = this.mesh(flat(2, 2), tex.circle, part.color);
        const glow = this.mesh(flat(2, 2), tex.dot, part.color);
        this.updaters.push((f) => {
          const y = this.top + 1.5 + Math.sin((f.time + seed) * 1.2) * 0.14;
          ring.position.y = y;
          glow.position.y = y - 0.05;
          ring.scale.set(s * 0.62, 1, s * 0.62);
          glow.scale.set(s * 0.8, 1, s * 0.8);
          (ring.material as THREE.MeshBasicMaterial).opacity = 0.9;
          (glow.material as THREE.MeshBasicMaterial).opacity = 0.22 + 0.08 * Math.sin((f.time + seed) * 0.9);
        });
        break;
      }
      case 'tint': {
        const wash = this.mesh(cardFlat(CW * 0.99, CH * 0.99), null, part.color, true, this.cardGroup);
        wash.position.y = 0.03;
        let top: THREE.Mesh | null = null;
        if (part.overlay) {
          const dark = part.overlay === 'cracks';
          const fit = dark ? CONFIG.playEffects.crackScale : 0.99; // cracks stay clear of the card's edges
          top = this.mesh(cardFlat(CW * fit, CH * fit), shapeTex(part.overlay), part.overlayColor ?? (dark ? '#2a1846' : '#ffffff'), true, this.cardGroup);
          top.position.y = 0.034;
        }
        this.updaters.push((f) => {
          const breathe = 0.93 + 0.07 * Math.sin((f.time + seed) * 0.8);
          (wash.material as THREE.MeshBasicMaterial).opacity = (part.min + (part.max - part.min) * f.k) * breathe;
          if (top) (top.material as THREE.MeshBasicMaterial).opacity = (0.2 + 0.75 * f.k) * breathe;
        });
        break;
      }
      case 'dome': {
        const geo = new THREE.SphereGeometry(1, 28, 12, 0, Math.PI * 2, 0, Math.PI / 2);
        const dome = this.mesh(geo, null, part.color, true, this.cardGroup);
        dome.scale.set(CW * 0.76, CH * 0.44, CH * 0.66);
        dome.position.y = 0.03;
        const rim = this.mesh(flat(2, 2), tex.circle, part.color, false, this.cardGroup);
        rim.scale.set(CW * 0.99, 1, CH * 0.86);
        rim.position.y = 0.04;
        this.updaters.push((f) => {
          const shimmer = 0.92 + 0.08 * Math.sin((f.time + seed) * 1.1);
          const strength = part.min + (part.max - part.min) * f.k;
          (dome.material as THREE.MeshBasicMaterial).opacity = strength * shimmer;
          (rim.material as THREE.MeshBasicMaterial).opacity = Math.min(1, 0.25 + strength * 1.2) * shimmer;
        });
        break;
      }
      case 'arcs': {
        const bolts = Array.from({ length: 4 }, () => this.mesh(flat(1.6, 1.6), shapeTex('bolt'), part.color, false, this.cardGroup));
        let wave = -1;
        this.updaters.push((f) => {
          const t = f.time + seed;
          const now = Math.floor(t / part.every);
          const into = t % part.every;
          if (now !== wave) {
            wave = now;
            for (const b of bolts) {
              b.position.set((Math.random() - 0.5) * CW * 0.8, 0.06, (Math.random() - 0.5) * CH * 0.8);
              b.rotation.y = Math.random() * Math.PI * 2;
              const size = 0.7 + Math.random() * 0.9;
              b.scale.set(size, 1, size);
            }
          }
          bolts.forEach((b, i) => {
            const flicker = Math.sin(t * 38 + i * 2.1) > 0.1;
            (b.material as THREE.MeshBasicMaterial).opacity = into < 0.7 && flicker ? 0.95 * (1 - into / 0.9) : 0;
          });
        });
        break;
      }
      case 'streaks': {
        // the Ambush arrival effect in miniature: three slashes, then two more in the second color.
        // They cross the card the token is on; with no card under it, they cross the token instead.
        // With `cross` (Frenzy) there are two waves on opposite diagonals, the second coming the other way a
        // moment after the first: an X drawn over the card.
        const holder = new THREE.Group();
        const angle = 0.62;
        const size = part.scale ?? 1;
        const speed = part.speed ?? 1;
        const plan = part.cross
          ? [
              { count: 2, length: 1.05, delay: 0, dur: 0.42, color: part.color, flip: 1 },
              { count: 2, length: 1.05, delay: (part.gap ?? 0.14) * speed, dur: 0.42, color: part.color2 ?? part.color, flip: -1 },
            ]
          : [
              { count: 3, length: 1.6, delay: 0, dur: 0.42, color: part.color, flip: 1 },
              { count: 2, length: 1.3, delay: 0.12, dur: 0.36, color: part.color2 ?? part.color, flip: 1 },
            ];
        const reach = part.cross ? 1.1 : 1.5; // how far each slash travels, in card lengths: an X stays over its card
        const waves = plan.map((w) => ({
          ...w,
          // flip 1: toward the far right corner. flip -1: the mirror image, toward the far left corner.
          dir: new THREE.Vector3(w.flip * Math.cos(angle), 0, -Math.sin(angle)),
          across: new THREE.Vector3(Math.sin(angle), 0, w.flip * Math.cos(angle)),
          lines: Array.from({ length: w.count }, (_, i) => {
            const m = this.mesh(flat(CH * w.length * size, 0.55 * Math.max(0.9, size)), tex.dot, w.color, false, holder);
            m.rotation.y = angle * w.flip;
            return { m, offset: (i - (w.count - 1) / 2) * 2.3 * size, start: i * 0.16 };
          }),
        }));
        this.updaters.push((f) => {
          const home = f.card ? this.cardGroup : this.group;
          if (holder.parent !== home) {
            home.add(holder);
            holder.scale.setScalar(f.card ? 1 : (s * 1.5) / CH);
          }
          const into = ((f.time + seed) % part.every) * speed; // a faster effect is the same one played quicker
          for (const w of waves) {
            const k = (into - w.delay) / w.dur;
            const span = 1 - (w.count - 1) * 0.16;
            for (const line of w.lines) {
              const local = Math.min(1, Math.max(0, (k - line.start) / span));
              line.m.visible = local > 0 && local < 1;
              if (!line.m.visible) continue;
              line.m.position.copy(w.dir).multiplyScalar((ease(local) - 0.5) * CH * reach * size).addScaledVector(w.across, line.offset);
              line.m.position.y = 1.4;
              (line.m.material as THREE.MeshBasicMaterial).opacity = Math.sin(Math.PI * local);
            }
          }
        });
        break;
      }
      case 'jitter': {
        const cracks = this.mesh(flat(s * 0.95 * CONFIG.playEffects.crackScale, s * 0.95 * CONFIG.playEffects.crackScale), shapeTex('cracks'), '#1a120a', true);
        cracks.position.y = this.top + 0.012;
        this.updaters.push((f) => {
          const into = (f.time + seed) % part.every;
          const shaking = into < 0.55;
          this.face.position.x = shaking ? Math.sin(f.time * 95) * 0.07 : 0;
          this.face.position.z = shaking ? Math.cos(f.time * 81) * 0.05 : 0;
          cracks.position.x = this.face.position.x;
          cracks.position.z = this.face.position.z;
          // cracks appear with the shudder, then fade away
          const appear = Math.min(1, into / 0.35);
          const fade = into < 1.6 ? 1 : Math.max(0, 1 - (into - 1.6) / 1.4);
          (cracks.material as THREE.MeshBasicMaterial).opacity = 0.9 * appear * fade;
        });
        break;
      }
      case 'pull': {
        const m = this.mesh(flat(2, 2), tex.circle, part.color, true);
        m.position.y = 0.03;
        this.updaters.push((f) => {
          const u = ((f.time + seed) % part.every) / 1.5;
          m.visible = u < 1;
          if (!m.visible) return;
          const size = s * (part.r * 2 - (part.r * 2 - 0.9) * ease(u));
          m.scale.set(size, 1, size);
          (m.material as THREE.MeshBasicMaterial).opacity = 0.75 * Math.sin(Math.PI * u);
        });
        break;
      }
    }
  }

  private buildMotes(part: Extract<FxPart, { t: 'motes' }>) {
    const s = this.size;
    const seed = this.seed;
    const map = shapeTex(part.shape);
    const a = new THREE.Color(part.color);
    const b = new THREE.Color(part.color2 ?? part.color);
    const life = part.life ?? 2;
    const cycle = part.every ?? life;

    if (part.path === 'out') {
      // flat on the table, pointing away from the token
      const items = Array.from({ length: part.count }, (_, i) => {
        const m = this.mesh(flat(part.size * 1.5, part.size), map, part.color, part.solid);
        const angle = (i / part.count) * Math.PI * 2 + seed;
        m.rotation.y = -angle;
        return { m, angle };
      });
      this.updaters.push((f) => {
        const v = scaled(part.grow, f.k);
        const u = ((f.time + seed) % cycle) / life;
        items.forEach(({ m, angle }, i) => {
          m.visible = u < 1 && i < Math.max(1, Math.round(part.count * v));
          if (!m.visible) return;
          const d = s * (0.45 + part.spread * (0.5 + 0.5 * v) * ease(u));
          m.position.set(Math.cos(angle) * d, 0.04, Math.sin(angle) * d);
          (m.material as THREE.MeshBasicMaterial).opacity = Math.sin(Math.PI * Math.min(1, u * 1.1)) * 0.9;
        });
      });
      return;
    }

    const trail = part.path === 'orbit' ? (part.trail ?? 0) : 0;
    const items = Array.from({ length: part.count }, (_, i) => ({
      sprites: Array.from({ length: 1 + trail }, () => this.sprite(map, part.color, part.solid)),
      angle: Math.random() * Math.PI * 2,
      reach: 0.5 + Math.random() * 0.5,
      mix: Math.random(),
      i,
    }));

    this.updaters.push((f) => {
      const v = scaled(part.grow, f.k);
      const shown = Math.max(1, Math.round(part.count * v));
      for (const it of items) {
        const on = it.i < shown;
        for (const sp of it.sprites) sp.visible = on;
        if (!on) continue;
        if (part.path === 'orbit') {
          const speed = part.speed ?? 1.5;
          it.sprites.forEach((sp, j) => {
            const ang = (it.i / part.count) * Math.PI * 2 + (f.time + seed) * speed - j * 0.16;
            const r = s * part.spread * (0.6 + 0.4 * v) * (0.92 + 0.08 * Math.sin(f.time * 0.7 + it.i));
            sp.position.set(Math.cos(ang) * r, this.top + 0.25 + 0.3 * Math.sin(f.time * 1.3 + it.i * 2), Math.sin(ang) * r);
            const fade = 1 - j / (trail + 1);
            sp.scale.setScalar(part.size * (0.55 + 0.45 * v) * (0.5 + 0.5 * fade));
            sp.material.color.copy(a).lerp(b, it.mix);
            sp.material.opacity = 0.85 * fade;
          });
          continue;
        }
        // rise: each one lifts off, drifts and fades; waves are spaced "every" seconds apart
        // fall: the same in reverse, sinking from up high down onto the token
        const sp = it.sprites[0];
        const u = ((f.time + seed + it.i * (part.every ? life * 0.3 : cycle / part.count)) % cycle) / life;
        sp.visible = u < 1;
        if (!sp.visible) continue;
        const r = s * part.spread * it.reach;
        const sway = Math.sin(f.time * 1.4 + it.i * 3) * 0.18;
        const lift = part.path === 'fall' ? 1 - u : u;
        sp.position.set(Math.cos(it.angle) * r + sway, this.top + 0.2 + (part.height ?? 2) * lift, Math.sin(it.angle) * r);
        sp.scale.setScalar(part.size * (0.6 + 0.4 * v) * (1 - 0.3 * u));
        sp.material.color.copy(a).lerp(b, it.mix);
        sp.material.opacity = Math.sin(Math.PI * Math.min(1, u * 1.05)) * 0.9;
      }
    });
  }

  /** Call every frame. */
  update(f: FxFrame) {
    if (f.card !== this.card) {
      this.cardGroup.removeFromParent();
      f.card?.add(this.cardGroup);
      this.card = f.card;
      this.cardGroup.visible = !!f.card;
    }
    for (const u of this.updaters) u(f);
  }

  dispose() {
    this.group.removeFromParent();
    this.cardGroup.removeFromParent();
    this.face.position.x = this.face.position.z = 0;
    for (const o of this.owned) {
      if (o instanceof THREE.Mesh) o.geometry.dispose();
      (o.material as THREE.Material).dispose();
    }
  }
}

// =====================================================================
//  Lanespanner: a thin glowing line joining tokens in the order they were placed.
// =====================================================================

export class LinkLines {
  private segments: THREE.Mesh[] = [];
  private parent: THREE.Object3D;
  private color: string;

  constructor(parent: THREE.Object3D, color: string) {
    this.parent = parent;
    this.color = color;
  }

  /** chains = lists of points to join up, one list per player */
  update(chains: THREE.Vector3[][], time: number) {
    let used = 0;
    for (const points of chains) {
      for (let i = 0; i + 1 < points.length; i++) {
        let seg = this.segments[used];
        if (!seg) {
          seg = new THREE.Mesh(
            flat(1, 0.55),
            new THREE.MeshBasicMaterial({ map: shapeTex('line'), color: this.color, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending }),
          );
          this.parent.add(seg);
          this.segments.push(seg);
        }
        const [a, b] = [points[i], points[i + 1]];
        seg.visible = true;
        seg.position.set((a.x + b.x) / 2, Math.max(a.y, b.y) + 0.12, (a.z + b.z) / 2);
        seg.rotation.y = -Math.atan2(b.z - a.z, b.x - a.x);
        seg.scale.set(Math.max(0.01, Math.hypot(b.x - a.x, b.z - a.z)), 1, 1);
        (seg.material as THREE.MeshBasicMaterial).opacity = 0.7 + 0.2 * Math.sin(time * 1.6 + used);
        used++;
      }
    }
    for (let i = used; i < this.segments.length; i++) this.segments[i].visible = false;
  }

  dispose() {
    for (const seg of this.segments) {
      seg.removeFromParent();
      seg.geometry.dispose();
      (seg.material as THREE.Material).dispose();
    }
    this.segments = [];
  }
}
